import '../App.css';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-toastify/dist/ReactToastify.css';

import { useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import { AppProviders } from './providers/AppProviders';
import { SembuhRoutes } from './router';
import useActivityTracker from 'shared/hooks/useActivityTracker';
import SessionWarningModal from 'shared/ui/session-warning-modal';
import { checkTokenAuth } from 'shared/lib/authUtils';

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const activityTracker = useActivityTracker(30 * 60 * 60 * 1000);

  const checkAuthStatus = () => {
    const token = checkTokenAuth();
    const newAuthStatus = !!token;

    if (newAuthStatus !== isAuthenticated) {
      setIsAuthenticated(newAuthStatus);
    }

    return newAuthStatus;
  };

  useEffect(() => {
    if (isAuthenticated && activityTracker) {
      activityTracker.startTracking();
    } else if (!isAuthenticated && activityTracker) {
      activityTracker.stopTracking();
    }
  }, [isAuthenticated, activityTracker]);

  useEffect(() => {
    checkAuthStatus();

    const authCheckInterval = setInterval(() => {
      checkAuthStatus();
    }, 5000);

    return () => clearInterval(authCheckInterval);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !activityTracker) return;

    const warningInterval = setInterval(() => {
      const lastActivity = activityTracker.getLastActivity();
      const timeSinceLastActivity = Date.now() - lastActivity;
      const warningTime = 175 * 60 * 1000;

      if (
        timeSinceLastActivity >= warningTime &&
        activityTracker.isUserActive()
      ) {
        // Session warning threshold reached — modal handles user-facing UX.
      }
    }, 60000);

    return () => clearInterval(warningInterval);
  }, [activityTracker, isAuthenticated]);

  // Detect chunk-load failures (caused by deploys invalidating old hashed
  // chunks) and prompt the user to reload. Listener registered once via
  // effect with cleanup so React 18 Strict Mode + re-renders don't stack
  // duplicate handlers (memory leak fix).
  useEffect(() => {
    const handleGlobalError = (e) => {
      const isChunkError =
        /Loading chunk [\d]+ failed/.test(e?.message ?? '') ||
        e?.message?.toLowerCase?.().includes('chunk') ||
        e?.filename?.includes?.('chunk');
      if (!isChunkError) return;
      if (
        window.confirm(
          'Telah ada update pada website. Silahkan reload halaman untuk mendapatkan update.\nKlik OK untuk reload.'
        )
      ) {
        window.location.reload(true);
      }
    };
    window.addEventListener('error', handleGlobalError);
    return () => window.removeEventListener('error', handleGlobalError);
  }, []);

  return (
    <AppProviders>
      <ToastContainer position="top-center" />
      <RouterProvider router={SembuhRoutes} />

      {isAuthenticated && activityTracker && (
        <SessionWarningModal activityTracker={activityTracker} />
      )}
    </AppProviders>
  );
};

export default App;
